import { IPersonaStyles } from '@fluentui/react';
import React, { FunctionComponent } from 'react';

import AppContext from 'App/AppContext';
import { IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import SingleTask from 'components/Tasks/SingleTask';
import DateDividerListItem, {
  DataGroup,
  DateComparer,
  DateDivider,
  insertDividersAndGroupsInList,
} from 'components/Utils/ListItemDateDividers';
import { getEntityIcon } from 'globalFunctions';
import { globalStackItemStylesPaddingSceneScroll, globalStackTokensGapExtraSmall } from 'globalStyles';
import Activity, { ActivityType } from 'models/activity';
import { EntityTypes } from 'models/entity';
import Task from 'models/tasks/task';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';
import { toNameInitial } from 'utils/string';
import { IWidgetRendererProps } from '../WidgetRenderer';
import { IPList } from "baseComponents/List/IPList";

interface IWidgetMyRecentItemsProps extends IWidgetRendererProps {}

const WidgetMyRecentItems: FunctionComponent<IWidgetMyRecentItemsProps> = (props: IWidgetMyRecentItemsProps) => {
  const { t } = useTranslation(['translation', 'dashboard']);
  const appContext = React.useContext(AppContext);
  const history = useHistory();
  const [recentItemsData, setRecentItemsData] = React.useState<Activity[] | undefined>(undefined);
  const [recentItemsDataDisplay, setRecentItemsDataDisplay] = React.useState<
    (Activity | DateDivider | DataGroup<Activity>)[] | undefined
  >(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedTask, setSelectedTask] = React.useState<Task | undefined>(undefined);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  React.useEffect(() => {
    if (recentItemsData && recentItemsData?.length > 0) {
      const newData = insertDividersAndGroupsInList(recentItemsData, getCompareDate, undefined, undefined);
      setRecentItemsDataDisplay(newData);
    }
  }, [recentItemsData]);

  const loadData = async (refresh?: boolean) => {
    try {
      if (props.onRefreshSharedData && props.widget.dashboard && refresh) {
        await props.onRefreshSharedData();

        return;
      }

      if (isLoading) return;
      setIsLoading(true);

      const activities = props.sharedData?.activities;

      setRecentItemsData(
        activities
          ?.filter((activity: Activity) =>
            [ActivityType.RecentlyChanged, ActivityType.RecentlyViewed].includes(activity.typeOfActivity),
          )
          .sort((a, b) => -1 * sortOnDate(a.created, b.created)),
      );
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCompareDate: DateComparer<Activity> = (a) => {
    return a.created;
  };

  const getTaskDetail = async (taskId: number) => {
    if (!taskId) {
      return;
    }

    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetSingleTask(taskId, false, accessToken, appContext.globalDataCache);
      if (tasks && tasks.tasks && tasks.tasks.length > 0) {
        const task = tasks.tasks[0];
        setSelectedTask(task);
      } else {
        appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const onEntityClickHandler = (activity: Activity) => {
    switch (activity.entity.typeOfEntity) {
      case EntityTypes.Requirement:
        history.push({ pathname: `/theme/${activity.entity.entityId}` });
        break;
      case EntityTypes.Control:
        history.push({ pathname: `/control/${activity.entity.entityId}` });
        break;
      case EntityTypes.Task:
        getTaskDetail(activity.entity.entityId);
        break;
      case EntityTypes.Risk:
        history.push({ pathname: `/risk/${activity.entity.entityId}` });
        break;
      case EntityTypes.Objective:
        history.push({ pathname: `/objective/${activity.entity.entityId}` });
        break;
      case EntityTypes.Process:
        history.push({ pathname: `/process/${activity.entity.entityId}` });
        break;
      case EntityTypes.KPI:
        history.push({ pathname: `/kpi/${activity.entity.entityId}` });
        break;
      case EntityTypes.Asset:
        history.push({ pathname: `/asset/${activity.entity.entityId}` });
        break;
      default:
        break;
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  //dismissing the modal is close's job: SingleTask also saves WITHOUT closing (a task type pane that
  //needs the stored task first), and clearing the selection here unmounts it in the middle of that
  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      await loadData(true);
    }
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
    await loadData(true);
  };

  const updateTask = async (task: Task) => {};

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 20 } }}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList
            items={recentItemsDataDisplay}
            onRenderCell={(activity) => {
              if (!activity) return null;

              if (activity instanceof DateDivider) {
                return <DateDividerListItem {...activity} />;
              }

              if (activity instanceof DataGroup) {
                return null;
              }

              return (
                <IPPersona
                  styles={personaStyle}
                  size={IPPersonaSize.size40}
                  text={activity.entity.entityName}
                  secondaryText={toLocaleDateTimeMedium(activity.created)}
                  imageInitials={toNameInitial(appContext.globalDataCache.users.get(activity.userId).name)}
                  onClick={() => onEntityClickHandler(activity)}
                  onRenderPrimaryText={() => (
                    <IPStack horizontal tokens={globalStackTokensGapExtraSmall} verticalAlign="center">
                      {getEntityIcon(activity.entity)}
                      <IPText>{activity.entity.entityName}</IPText>
                    </IPStack>
                  )}
                />
              );
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {recentItemsData?.length === 0 && (
        <IPStack.Item>
          <IPText>{t('dashboard:RecentItems.NoItems')}</IPText>
        </IPStack.Item>
      )}
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};

export default WidgetMyRecentItems;
